import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, MessageSquare, Phone } from "lucide-react";
import Logo from "./Logo";

export default function Footer() {
  useEffect(() => {
    // Inject UserWay Accessibility Widget on user interaction to improve performance
    const loadWidget = () => {
      if (document.querySelector('script[src="https://cdn.userway.org/widget.js"]')) return;
      
      const d = document;
      const s = d.createElement("script");
      s.setAttribute("data-position", "5");
      s.setAttribute("data-account", "K8zqCZIKI3");
      s.setAttribute("src", "https://cdn.userway.org/widget.js");
      s.defer = true;
      (d.body || d.head).appendChild(s);
    };

    const triggerEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handleInteraction = () => {
      loadWidget();
      triggerEvents.forEach(e => window.removeEventListener(e, handleInteraction));
    };

    triggerEvents.forEach(e => window.addEventListener(e, handleInteraction, { once: true, passive: true }));
    const timeoutId = setTimeout(handleInteraction, 5000); // Fallback

    return () => {
      triggerEvents.forEach(e => window.removeEventListener(e, handleInteraction));
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <footer className="bg-primary text-primary-foreground/60 py-12 border-t border-white/10 mt-auto">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="inline-block shrink-0 mb-6 hover:opacity-90 transition-opacity" aria-label="Woods Spine and Wellness Home" onClick={() => window.scrollTo(0, 0)}>
              <Logo className="h-12" variant="dark" />
            </Link>
            <p className="text-sm max-w-xs text-primary-foreground/80 mb-6">
              Dedicated to providing advanced, non-invasive therapies to help you live a pain-free, active life.
            </p>
            <div className="flex gap-4 mb-4 flex-wrap">
              <a href="tel:+19032158828" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-accent hover:text-white transition-colors" aria-label="Call Us">
                <Phone className="h-5 w-5" />
              </a>
              <a href="sms:+19032158828" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-accent hover:text-white transition-colors" aria-label="Text Us">
                <MessageSquare className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-accent hover:text-white transition-colors" aria-label="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-accent hover:text-white transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>

              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-accent hover:text-white transition-colors" aria-label="TikTok">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 2.23-.9 4.4-2.42 5.92-1.48 1.48-3.55 2.3-5.66 2.47-1.99.16-4.04-.21-5.74-1.31-1.68-1.09-2.92-2.81-3.32-4.78-.45-2.22-.05-4.61 1.15-6.49 1.16-1.8 3.03-3.05 5.1-3.41 1.05-.18 2.14-.18 3.2-.04V13.4c-.6-.1-1.21-.11-1.81-.03-.96.13-1.85.67-2.42 1.44-.56.77-.79 1.76-.64 2.71.16.98.81 1.83 1.66 2.27.85.44 1.87.53 2.81.25.9-.27 1.66-.9 2.1-1.74.43-.82.63-1.76.6-2.69-.03-3.23-.01-6.46-.02-9.69C12.5 5.02 12.52 2.52 12.525.02z" />
                </svg>
              </a>
            </div>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-accent hover:underline transition-colors">About Us</Link></li>
              <li><Link to="/blog" className="text-accent hover:underline transition-colors">Blog</Link></li>
              <li><Link to="/financials" className="text-accent hover:underline transition-colors">Financials</Link></li>
              <li><Link to="/contact" className="text-accent hover:underline transition-colors">Contact</Link></li>
              <li><a href="https://intake.helloinnate.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline transition-colors font-medium">Online Paperwork</a></li>
              <li><Link to="/services" className="text-accent hover:underline transition-colors">Services</Link></li>
              <li><Link to="/testimonials" className="text-accent hover:underline transition-colors">Testimonials</Link></li>
              <li><Link to="/what-we-treat" className="text-accent hover:underline transition-colors">What We Treat</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services" className="text-accent hover:underline transition-colors">All Services</Link></li>
              <li><Link to="/longview-chirothin-weight-loss" className="text-accent hover:underline transition-colors">ChiroThin Weight Loss</Link></li>
              <li><Link to="/longview-spinal-decompression" className="text-accent hover:underline transition-colors">DRX9000 Spinal Decompression</Link></li>
              <li><Link to="/longview-red-light-therapy" className="text-accent hover:underline transition-colors">Red Light Therapy</Link></li>
              <li><Link to="/longview-softwave-pain-management-tissue-regeneration" className="text-accent hover:underline transition-colors">SoftWave TRT</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">Resources</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/accessibility" className="text-accent hover:underline transition-colors">Accessibility</Link></li>
              <li><Link to="/ai-disclaimer" className="text-accent hover:underline transition-colors">AI Disclaimer</Link></li>
              <li><Link to="/anti-discrimination" className="text-accent hover:underline transition-colors">Anti-Discrimination</Link></li>
              <li><Link to="/goodfaithestimate" className="text-accent hover:underline transition-colors">Good Faith Estimate</Link></li>
              <li><Link to="/healthcare-disclaimer" className="text-accent hover:underline transition-colors">Healthcare Disclaimer</Link></li>
              <li><Link to="/hipaa" className="text-accent hover:underline transition-colors">HIPAA Privacy Notice</Link></li>
              <li><Link to="/privacy-policy" className="text-accent hover:underline transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms-service" className="text-accent hover:underline transition-colors">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t border-white/10 text-sm text-center flex flex-col items-center gap-2">
          <p>&copy; {new Date().getFullYear()} Woods Spine & Wellness. All rights reserved.</p>
          <p className="text-primary-foreground/60">
            Powered by <a href="https://www.velocesolutions.net/" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-white transition-colors font-medium">Veloce Solutions</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
